import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInterceptor';
import { Loader2, X, CheckCircle, Clock, Camera, Mic, Monitor, Check, ChevronRight, ChevronLeft, Play, Square, RotateCcw, Volume2 } from 'lucide-react';
import InterviewPage from './interviewPage';


interface InterviewDetails {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  duration?: number;
  questionSets?: any[];
  evaluationCriteria?: any;
}

interface ValidationData {
  valid: boolean;
  candidateId: string;
  assessmentId: string;
  interviewDetails: InterviewDetails;
  status: string;
  userExists: boolean;
}

const Interview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const assessmentId = searchParams.get('assessmentId');
  const candidateId = searchParams.get('candidateId');
  const sessionId = searchParams.get('sessionId');

  // Helper function to check if content contains HTML
  const containsHTML = (text: string): boolean => {
    if (!text) return false;
    // Check for HTML tags (more precise detection)
    const htmlRegex = /<[a-z][\s\S]*?>/i;
    return htmlRegex.test(text.trim());
  };

  // Validation state
  const [isValidatingSession, setIsValidatingSession] = useState(true);
  const [isValidationError, setIsValidationError] = useState(false);
  const [validationError, setValidationError] = useState<any>(null);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [showInterviewPage, setShowInterviewPage] = useState(false);
  const [showScreenSelectionErrorModal, setShowScreenSelectionErrorModal] = useState(false);
  const [showSingleDisplayConfirmModal, setShowSingleDisplayConfirmModal] = useState(false);
  const [singleDisplayConfirmCallback, setSingleDisplayConfirmCallback] = useState<((confirmed: boolean) => void) | null>(null);
  const [currentStep, setCurrentStep] = useState(1); // 1 = guidelines, 2 = hardware check
  const [hardwareSubStep, setHardwareSubStep] = useState<'camera' | 'microphone' | 'screen' | 'summary'>('camera'); // Sub-steps for hardware check
  const [guidelinesChecked, setGuidelinesChecked] = useState<boolean[]>([]);
  const [hardwareStatus, setHardwareStatus] = useState({
    camera: false,
    microphone: false,
    screen: false,
    systemAudio: false
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [audioElementRef, setAudioElementRef] = useState<HTMLAudioElement | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraVideoRef, setCameraVideoRef] = useState<HTMLVideoElement | null>(null);
  const startingInterviewRef = useRef(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [screenVideoRef, setScreenVideoRef] = useState<HTMLVideoElement | null>(null);
  const [showBypassButton, setShowBypassButton] = useState(false);

  // Initialize guidelines checkboxes (5 guidelines)
  useEffect(() => {
    if (showGuidelinesModal) {
      setGuidelinesChecked(new Array(5).fill(true));
      setCurrentStep(1);
      setHardwareSubStep('camera');
      // Always reset hardware status to false when modal opens
      setHardwareStatus({ camera: false, microphone: false, screen: false, systemAudio: false });
      // Reset screen stream
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
      // Reset recording state
      setIsRecording(false);
      setRecordedAudio(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      if (audioElement) {
        audioElement.pause();
        setAudioElement(null);
      }
      setIsPlaying(false);
      // Stop camera stream if exists
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      // Stop screen stream if exists
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
    }
  }, [showGuidelinesModal]);

  // Update video element when camera stream changes
  useEffect(() => {
    if (cameraVideoRef && cameraStream) {
      cameraVideoRef.srcObject = cameraStream;
      cameraVideoRef.play().catch(err => {
        console.error('Video play error:', err);
      });
    }
    
    return () => {
      // Don't stop camera when switching to InterviewPage (needed for recording)
      if (startingInterviewRef.current) return;
      if (cameraVideoRef && cameraVideoRef.srcObject) {
        const stream = cameraVideoRef.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        cameraVideoRef.srcObject = null;
      }
    };
  }, [cameraStream, cameraVideoRef]);

  // Update video element when screen stream changes
  useEffect(() => {
    if (screenVideoRef && screenStream) {
      console.log('Setting screen stream to video element, stream active:', screenStream.active);
      const videoElement = screenVideoRef;
      videoElement.srcObject = screenStream;
      
      // Ensure video plays
      videoElement.play().then(() => {
        console.log('Screen video playback started successfully');
      }).catch(err => {
        console.error('Screen video play error:', err);
      });
      
      // Don't clear in cleanup - let the stream persist
      // The cleanup was causing the stream to disappear
    } else if (screenVideoRef && !screenStream) {
      // Clear video element when stream is removed
      screenVideoRef.srcObject = null;
    }
  }, [screenStream, screenVideoRef]);

  // Reset screen status when entering screen step (only once, not when stream changes)
  useEffect(() => {
    if (hardwareSubStep === 'screen') {
      // Only reset screen status to false when first entering screen step
      // Don't reset if stream already exists (user might be returning to this step)
      if (!screenStream) {
        setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
      }
    }
  }, [hardwareSubStep]); // Only depend on hardwareSubStep, not screenStream

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioElement) {
        audioElement.pause();
      }
    };
  }, [audioUrl, audioElement]);

  // Hardware check functions - Request permissions from user
  const checkHardware = async () => {
    // Only check screen sharing availability, don't auto-verify camera/microphone
    // Camera and microphone will be verified through user interaction
    setHardwareStatus(prev => ({ 
      ...prev, 
      screen: typeof navigator.mediaDevices.getDisplayMedia !== 'undefined' 
    }));
  };

  const startCameraPreview = async () => {
    try {
      // Stop any existing camera stream first to ensure fresh permission prompt
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => {
          track.stop();
        });
        setCameraStream(null);
        // Small delay to ensure the stream is fully released
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Request camera permission - browser will show permission prompt with device selection
      // By not specifying deviceId, the browser will show the native device selection UI
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user', // Prefer front-facing camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      // Check if we actually got video tracks
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0) {
        setCameraStream(stream);
        // Video element will be updated via useEffect when cameraStream changes
        } else {
        setHardwareStatus(prev => ({ ...prev, camera: false }));
        alert('Camera permission granted but no video track found');
      }
    } catch (error: any) {
      setHardwareStatus(prev => ({ ...prev, camera: false }));
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Camera permission was denied. Please allow camera access in your browser settings to continue.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert('No camera found. Please connect a camera and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        alert('Camera is already in use by another application. Please close other applications using the camera.');
        } else {
        alert('Unable to access camera. Please check your camera settings.');
      }
    }
  };

  const confirmCamera = () => {
    if (cameraStream) {
      setHardwareStatus(prev => ({ ...prev, camera: true }));
      // Reset microphone state when moving to microphone step
      setHardwareStatus(prev => ({ ...prev, microphone: false }));
      setRecordedAudio(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      setIsRecording(false);
      setIsPlaying(false);
      setHardwareSubStep('microphone');
      // Don't stop the stream yet, keep it for the interview
    }
  };

  const checkCamera = async () => {
    // This function is kept for backward compatibility but not used in new flow
    await startCameraPreview();
  };

  const checkMicrophone = async () => {
    try {
      // Request microphone permission - browser will show permission prompt
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      // Check if we actually got audio tracks
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        // Don't stop the stream yet - we'll use it for recording
        // Store stream reference for recording
        setHardwareStatus(prev => ({ ...prev, microphone: true }));
        // The stream will be used in startRecording function
        return stream;
            } else {
        setHardwareStatus(prev => ({ ...prev, microphone: false }));
        alert('Microphone permission granted but no audio track found');
      return null;
            }
          } catch (error: any) {
      setHardwareStatus(prev => ({ ...prev, microphone: false }));
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Microphone permission was denied. Please allow microphone access in your browser settings to continue.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        alert('Microphone is already in use by another application. Please close other applications using the microphone.');
            } else {
        alert('Unable to access microphone. Please check your microphone settings.');
      }
      return null;
    }
  };

  const startMicrophoneRecording = async () => {
    try {
      // Request microphone permission and get stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      // Determine the best supported MIME type for recording
      // Note: We'll record in webm but ensure it's playable
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      }

      // Create MediaRecorder with the best supported format
      const recorder = new MediaRecorder(stream, {
        mimeType: mimeType as any,
        audioBitsPerSecond: 128000 // Good quality for voice
      });

      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        if (chunks.length > 0) {
          // Create blob with the recorded format
          const originalBlob = new Blob(chunks, { type: mimeType });
          setRecordedAudio(originalBlob);
          
          // Create object URL for playback
          // Use the blob directly - modern browsers should support webm
          const url = URL.createObjectURL(originalBlob);
          setAudioUrl(url);
          
          console.log('Recording stopped, blob created:', {
            size: originalBlob.size,
            type: originalBlob.type,
            chunks: chunks.length
      });
    } else {
          console.error('No audio chunks recorded');
          alert('Recording failed - no audio data captured. Please try again.');
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event);
        alert('Recording error occurred. Please try again.');
        setIsRecording(false);
      };

      // Start recording with timeslice to ensure data is available
      recorder.start(100); // Collect data every 100ms
      setMediaRecorder(recorder);
      setIsRecording(true);
      console.log('Recording started with MIME type:', mimeType);
    } catch (error: any) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Please check microphone permissions.');
      setIsRecording(false);
    }
  };

  const stopMicrophoneRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const playRecordedAudio = async () => {
    if (!recordedAudio) {
      console.error('No recorded audio available');
      alert('No recording available to play. Please record first.');
        return;
      }
      
    // Stop any existing playback
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.src = '';
    }

    try {
      // Revoke old URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      // Create a fresh blob URL
      const freshAudioUrl = URL.createObjectURL(recordedAudio);
      setAudioUrl(freshAudioUrl);

      console.log('Playing audio:', {
        blobSize: recordedAudio.size,
        blobType: recordedAudio.type
      });

      // Create new audio element using the Audio constructor with the URL directly
      const audio = new Audio(freshAudioUrl);
      
      // Set up event handlers before attempting to play
      audio.onplay = () => {
        setIsPlaying(true);
        console.log('✅ Audio playback started');
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        setAudioElement(null);
        console.log('✅ Audio playback ended');
      };
      
      audio.onpause = () => {
        setIsPlaying(false);
        console.log('⏸️ Audio playback paused');
      };
      
      audio.onerror = (error) => {
        console.error('❌ Audio playback error:', {
          error: error,
          errorCode: audio.error?.code,
          errorMessage: audio.error?.message,
          src: audio.src,
          blobType: recordedAudio.type,
          blobSize: recordedAudio.size
        });
        setIsPlaying(false);
        setAudioElement(null);
        alert('Failed to play recording. The audio format may not be supported by your browser. Please try recording again.');
      };
      
      audio.onloadeddata = () => {
        console.log('✅ Audio data loaded, duration:', audio.duration);
      };
      
      audio.oncanplay = () => {
        console.log('✅ Audio can play - ready');
      };
      
      audio.oncanplaythrough = () => {
        console.log('✅ Audio can play through');
      };
      
      audio.onloadstart = () => {
        console.log('🔄 Audio loading started');
      };
      
      audio.onstalled = () => {
        console.warn('⚠️ Audio loading stalled');
      };
      
      audio.onabort = () => {
        console.warn('⚠️ Audio loading aborted');
      };
      
      // Attempt to play
      try {
        await audio.play();
        setAudioElement(audio);
        console.log('✅ Audio play() called successfully');
      } catch (playError: any) {
        console.error('❌ Error calling audio.play():', playError);
        setIsPlaying(false);
        setAudioElement(null);
        
        // Check if it's a format issue
        if (playError.name === 'NotSupportedError' || playError.message?.includes('not supported')) {
          alert('Your browser does not support playing this audio format. Please try using Chrome or Firefox browser.');
        } else {
          alert('Failed to play recording. Please try recording again.');
        }
      }
    } catch (error: any) {
      console.error('Error setting up audio playback:', error);
      alert('Failed to prepare audio playback. Please try recording again.');
      setIsPlaying(false);
    }
  };

  const stopPlayback = () => {
    if (audioElementRef) {
      audioElementRef.pause();
      audioElementRef.currentTime = 0;
      setIsPlaying(false);
    }
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setIsPlaying(false);
      setAudioElement(null);
    }
  };

  const resetRecording = () => {
    stopPlayback();
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setRecordedAudio(null);
    setAudioUrl(null);
    setHardwareStatus(prev => ({ ...prev, microphone: false }));
  };

  const startScreenShare = async () => {
    if (typeof navigator.mediaDevices === 'undefined' || typeof navigator.mediaDevices.getDisplayMedia === 'undefined') {
      alert('Screen sharing is not supported in your browser. Please use Chrome, Firefox, or Edge.');
          return;
        }
        
    try {
      console.log('Requesting screen share permission...');
      
      // Request screen sharing permission - browser will show native permission dialog
      // This will trigger the browser's screen sharing picker
      // Add constraints to reduce lag: limit frame rate and resolution
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor', // Prefer full monitor/display
          frameRate: { ideal: 24, max: 24 }, // Limit to 24fps to reduce CPU usage
          width: { ideal: 1280, max: 1280 }, // Limit max width to reduce processing (720p)
          height: { ideal: 720, max: 720 } // Limit max height to reduce processing (720p)
        } as MediaTrackConstraints,
        audio: true // Capture system audio if available
      });
      
      console.log('Screen share permission granted, stream received:', stream);
      
      // Check if we got video tracks
      const videoTracks = stream.getVideoTracks();
      console.log('Video tracks:', videoTracks.length);
      
      if (videoTracks.length > 0) {
        const track = videoTracks[0];
        
        // Apply constraints to reduce lag - limit frame rate and resolution
        try {
          await track.applyConstraints({
            frameRate: { ideal: 24, max: 24 },
            width: { ideal: 1280, max: 1280 },
            height: { ideal: 720, max: 720 }
          });
          console.log('Applied constraints to video track to reduce lag');
        } catch (constraintError) {
          console.warn('Could not apply all constraints:', constraintError);
          // Continue even if constraints fail - browser will use defaults
        }
        
        const settings = track.getSettings();
        
        // Type assertion for displaySurface (not in standard TypeScript definitions yet)
        const settingsWithDisplay = settings as MediaTrackSettings & { displaySurface?: 'monitor' | 'window' | 'browser' };
        
        console.log('Screen track settings:', {
          displaySurface: settingsWithDisplay.displaySurface,
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate,
          trackState: track.readyState
        });
        
        // Validate that user selected "Entire Screen" (monitor) only
        if (settingsWithDisplay.displaySurface !== 'monitor') {
          // Stop the stream if user selected window or browser tab
          stream.getTracks().forEach(t => t.stop());
          setShowScreenSelectionErrorModal(true);
          setScreenStream(null);
          setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
          return;
        }
        
        // Validate that system audio is available (mandatory)
        const audioTracks = stream.getAudioTracks();
        console.log('Audio tracks:', audioTracks.length);
        
        if (audioTracks.length === 0) {
          // System audio is mandatory - stop the stream and show error
          stream.getTracks().forEach(t => t.stop());
          setScreenStream(null);
          setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
          alert('System audio is required to proceed. Please ensure you select "Share system audio" or "Share tab audio" when sharing your screen, and grant audio permissions.');
          return;
        }
        
        console.log('System audio track found:', {
          label: audioTracks[0].label,
          enabled: audioTracks[0].enabled,
          readyState: audioTracks[0].readyState
        });
        
        // Store the stream for preview - this should trigger the useEffect
        console.log('Setting screen stream state...');
        setScreenStream(stream);
        console.log('Screen stream state set, should trigger re-render');
        
        // Listen for when user stops sharing (closes the share dialog)
        track.onended = () => {
          console.log('Screen sharing ended by user');
          setScreenStream(null);
          setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
        };
        
        // Listen for when audio track ends
        audioTracks[0].addEventListener('ended', () => {
          console.log('System audio track ended');
          setHardwareStatus(prev => ({ ...prev, systemAudio: false }));
        });
                } else {
        alert('Screen sharing permission granted but no video track found. Please try again.');
        setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
              }
            } catch (error: any) {
      console.error('Screen share error:', error);
      setScreenStream(null);
      setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Screen sharing permission was denied. Please allow screen sharing to continue with the interview.');
      } else if (error.name === 'NotReadableError') {
        alert('Unable to access your screen. Please check your system permissions and try again.');
      } else if (error.name === 'AbortError' || error.name === 'NotFoundError') {
        // User cancelled or no source available - don't show error, just log
        console.log('User cancelled screen share or no source available');
              } else {
        alert(`Unable to access screen sharing: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  const confirmScreenShare = async () => {
    if (!screenStream) {
      alert('Please start screen sharing first.');
      return;
    }

    try {
      const videoTracks = screenStream.getVideoTracks();
      if (videoTracks.length === 0) {
        alert('No screen track available. Please try sharing again.');
        return;
      }

      const track = videoTracks[0];
      const settings = track.getSettings();
      const capabilities = track.getCapabilities();
      
      // Type assertion for displaySurface (not in standard TypeScript definitions yet)
      const settingsWithDisplay = settings as MediaTrackSettings & { displaySurface?: 'monitor' | 'window' | 'browser' };
      
      console.log('Verifying single display:', {
        displaySurface: settingsWithDisplay.displaySurface,
        width: settings.width,
        height: settings.height
      });

      // Check display surface type
      // 'monitor' = full display/monitor
      // 'window' = application window
      // 'browser' = browser tab
      const displaySurface = settingsWithDisplay.displaySurface;
      
      if (displaySurface !== 'monitor') {
        // This should have been caught earlier, but just in case, handle it
        // This case is already handled in startScreenShare, so this shouldn't happen here
        console.warn('Non-monitor display surface detected in confirmScreenShare:', displaySurface);
      }

      // Additional verification: Check screen dimensions heuristically
      // This is not 100% reliable but can provide a hint
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const trackWidth = settings.width || 0;
      const trackHeight = settings.height || 0;
      
      // Heuristic: If shared screen is significantly larger than browser window's screen,
      // or if dimensions are unusually large (>4K), it might indicate multiple displays
      // Note: This is just a hint, not a definitive check
      const isPotentiallyMultipleDisplays = trackWidth > 3840 || trackHeight > 2160 || 
        (trackWidth > screenWidth * 1.5) || (trackHeight > screenHeight * 1.5);
      
      console.log('Display check heuristic:', {
        screenDimensions: { width: screenWidth, height: screenHeight },
        trackDimensions: { width: trackWidth, height: trackHeight },
        potentiallyMultiple: isPotentiallyMultipleDisplays
      });
      
      // Ask user to confirm they only have one display using modal
      const hasSingleDisplay = await new Promise<boolean>((resolve) => {
        setSingleDisplayConfirmCallback(() => resolve);
        setShowSingleDisplayConfirmModal(true);
      });

      if (!hasSingleDisplay) {
        // Show error modal instead of alert
        setShowSingleDisplayConfirmModal(false);
        setShowScreenSelectionErrorModal(true);
        // Stop the current share
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
        return;
      }
      
      setShowSingleDisplayConfirmModal(false);

      // Validate that system audio is available (mandatory)
      const audioTracks = screenStream.getAudioTracks();
      if (audioTracks.length === 0) {
        // System audio is mandatory - stop the stream and show error
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
        setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
        alert('System audio is required to proceed. Please ensure you select "Share system audio" or "Share tab audio" when sharing your screen, and grant audio permissions.');
        return;
      }

      // Verification passed - both screen and system audio are available
      setHardwareStatus(prev => ({ ...prev, screen: true, systemAudio: true }));
      
      // Keep the stream active for the interview (don't stop it yet)
      // The stream will be used during the actual interview
      console.log('Screen sharing verified and confirmed');
    } catch (error: any) {
      console.error('Error confirming screen share:', error);
      alert('Error verifying screen share. Please try again.');
    }
  };

  const checkScreen = async () => {
    // This function is kept for backward compatibility but startScreenShare is used instead
    await startScreenShare();
  };

  useEffect(()=>{
    async function isExistingUser(){
      if (!sessionId) return;
      
      try {
        const response = await axiosInstance.get(`/direct/is-existing-user?sessionId=${sessionId}`);
        if (response.data.success) {
          const {userExists,email,phone} = response.data.data;

          if(!userExists){
            // Redirect to the assessment URL in the current window
            window.location.href = `${process.env.REACT_APP_ASSESSMENT_URL}/jointalentpool/public/68c42a766d9630692f685496?userPhone=${phone}`;
            return; 
          }
        }
      } catch (error) {
        console.error("Error checking if user exists:", error);
      }
    }

    isExistingUser()
  },[sessionId])

  // Apply primary color to Start Interview buttons
  useEffect(() => {
    const applyButtonColor = () => {
      const button = document.getElementById('start-interview-button');
      if (button) {
        button.style.setProperty('background-color', '#f5945c', 'important');
        button.style.setProperty('border', 'none', 'important');
      }
      
      // Also find button by text content
      const allButtons = document.querySelectorAll('button');
      allButtons.forEach((btn) => {
        if (btn.textContent?.includes('Start Interview') && !btn.id) {
          btn.style.setProperty('background-color', '#f5945c', 'important');
          btn.style.setProperty('border', 'none', 'important');
        }
      });
    };
    
    applyButtonColor();
    // Re-apply after a short delay to ensure DOM is ready
    const timeout = setTimeout(applyButtonColor, 100);
    return () => clearTimeout(timeout);
  }, [showGuidelinesModal, hardwareSubStep]);

  // Validate session and userId on startup
  useEffect(() => {
    const validateSession = async () => {
      if (!sessionId) {
        setIsValidatingSession(false);
      return;
    }

      try {
        setIsValidatingSession(true);
        setIsValidationError(false);
        setValidationError(null);
        console.log('Validating session...');
        
        const response = await axiosInstance.get('/direct/validate-session', {
          params: { 
            sessionId,
          }
        });
        
        if (response.data.success) {
          console.log('Session validated successfully:', response.data.data);
          const { valid, candidateId, assessmentId, interviewDetails, status, userExists } = response.data.data.data;
          
          console.log(response.data.data,"===")
          // Store validation data
          setValidationData({
            valid,
            candidateId,
            assessmentId,
            interviewDetails,
            status,
            userExists
          });

          if(!userExists){
            // user not exists in assessment portal going to portal for registration
            console.log("no user found , navigating to ",`${process.env.ASSESSMENT_URL}/jointalentpool/public/68c42a766d9630692f685496`)
            

          }
          
          // Show modal with interview details
          setShowDetailsModal(true);
        }
        else{
         alert(response.data.message);
        }
       

        // Validation successful
        setIsValidatingSession(false);
      } catch (error: any) {
        // Validation failed
        setIsValidatingSession(false);
        setIsValidationError(true);
        setValidationError(error);
      }
    };

    validateSession();
  }, [sessionId]);

  // Show loading screen while validating session
  if (isValidatingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Validating Session</h2>
          <p className="text-gray-600">Please wait while we verify your session...</p>
        </div>
      </div>
    );
  }

  // Show error screen if validation failed
  if (isValidationError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 text-gray-900 flex items-center justify-center">
        <div className="max-w-md bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-orange-200">
          <h2 className="text-2xl font-bold mb-2 text-gray-900">Session Validation Failed</h2>
          <p className="text-gray-600 mb-4">
            {(validationError as any)?.response?.data?.message || (validationError as any)?.message || 'Invalid session or user ID'}
          </p>
        </div>
      </div>
    );
  }

  // Show loading if user is not loaded yet
  // if (!user) {
  //   return (
  //     <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 text-gray-900 flex items-center justify-center">
  //       <div className="text-center">
  //         <Loader2 className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
  //         <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading User</h2>
  //         <p className="text-gray-600">Please wait...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Show InterviewPage component if interview has started
  if (showInterviewPage) {
    const handleInterviewEnd = () => {
      startingInterviewRef.current = false;
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
    };
    return <InterviewPage sessionId={sessionId} cameraStream={cameraStream} screenStream={screenStream} onInterviewEnd={handleInterviewEnd} />;
  }

  // After validation passes, show success with modal
  return (
    <>
      <div className="min-h-screen bg-gray-50 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Validated</h2>
          <p className="text-gray-600 mb-6">Interview session is ready.</p>
          {validationData && (
            <button
              onClick={() => setShowDetailsModal(true)}
              className="bg-gray-800 hover:bg-gray-900 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              View Interview Details
            </button>
          )}
  </div>
</div>

      {/* Interview Details Modal */}
      {showDetailsModal && validationData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Interview Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
</div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Interview Title */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {validationData?.interviewDetails?.title || 'Interview'}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
                    {validationData?.interviewDetails?.difficulty}
  </span>
                  {validationData?.interviewDetails?.duration && (
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{validationData?.interviewDetails?.duration} minutes</span>
</div>
                  )}
            </div>
          </div>

              {/* Description */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                {containsHTML(validationData?.interviewDetails?.description || '') ? (
                  <div 
                    className="bg-gray-50 rounded p-3 text-sm text-gray-600 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: validationData?.interviewDetails?.description || 'No description available' }}
                  />
                ) : (
                  <div className="bg-gray-50 rounded p-3 text-sm text-gray-600 whitespace-pre-wrap break-words">
                    {validationData?.interviewDetails?.description || 'No description available'}
                  </div>
                )}
            </div>

              {/* Session Info */}
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <p className="text-sm font-medium text-gray-900">
                    {validationData?.status?.charAt(0).toUpperCase() + validationData?.status?.slice(1).replace('_', ' ')}
                            </p>
                          </div>
                          </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2.5 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
                            <button
                              onClick={() => {
                    setShowDetailsModal(false);
                    setShowGuidelinesModal(true);
                              }}
                  className="flex-1 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                  style={{ 
                    backgroundColor: '#f5945c',
                    border: 'none'
                  } as React.CSSProperties}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                  }}
                            >
                  Start Interview
                            </button>
                        </div>
                        </div>
                          </div>
                        </div>
                      )}

      {/* Guidelines & Hardware Check Modal (Multi-step) */}
      {showGuidelinesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 1 ? 'text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                  style={currentStep >= 1 ? { backgroundColor: '#f5945c' } : {}}
                  >
                    1
                            </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 2 ? 'text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                  style={currentStep >= 2 ? { backgroundColor: '#f5945c' } : {}}
                  >
                    2
                        </div>
                    </div>
                <span className="text-sm text-gray-500 ml-2">
                  {currentStep === 1 ? 'Guidelines' : 'Hardware Check'}
                </span>
              </div>
                        <button
                          onClick={() => {
                  setShowGuidelinesModal(false);
                  setCurrentStep(1);
                  setGuidelinesChecked([]);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
                        </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Guidelines</h3>
                  
                  <div className="space-y-3">
                    {[
                      "Ensure you have a stable internet connection throughout the interview",
                      "Find a quiet environment free from distractions and background noise",
                      "Keep your camera and microphone enabled at all times during the interview",
                      "Do not use any external help, notes, or communication devices during the interview",
                      "Review all questions carefully and take your time to provide thoughtful answers"
                    ].map((guideline, index) => (
                      <label
                        key={index}
                        className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={guidelinesChecked[index] || false}
                          onChange={(e) => {
                            const newChecked = [...guidelinesChecked];
                            newChecked[index] = e.target.checked;
                            setGuidelinesChecked(newChecked);
                          }}
                          className="mt-1 h-4 w-4 text-gray-800 border-gray-300 rounded focus:ring-gray-500"
                        />
                        <span className="text-sm text-gray-700 flex-1">{guideline}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200 mt-6">
                      <button
                        onClick={() => {
                        if (guidelinesChecked.every(checked => checked)) {
                          setCurrentStep(2);
                          checkHardware();
                        }
                      }}
                      disabled={!guidelinesChecked.every(checked => checked)}
                      className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      style={!guidelinesChecked.every(checked => checked) ? {} : { backgroundColor: '#f5945c', border: 'none' }}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                        }
                      }}
                    >
                      <span>Continue</span>
                      <ChevronRight className="h-4 w-4" />
                      </button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Hardware Check</h3>
                   {showBypassButton && <button
                      type="button"
                      onClick={() => {
                        if (cameraStream) {
                          cameraStream.getTracks().forEach(track => track.stop());
                          setCameraStream(null);
                        }
                        if (screenStream) {
                          screenStream.getTracks().forEach(track => track.stop());
                          setScreenStream(null);
                        }
                        setShowGuidelinesModal(false);
                        setCurrentStep(1);
                        setGuidelinesChecked([]);
                        setHardwareSubStep('camera');
                        setShowInterviewPage(true);
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Bypass all checks →
                    </button>}
                  </div>
                  
                  {hardwareSubStep === 'camera' && (
                    <div className="space-y-4">
                      {/* Camera Preview */}
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3 mb-4">
                          <Camera className={`h-5 w-5 ${hardwareStatus.camera ? 'text-green-600' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium text-gray-900">Camera Check</p>
                            <p className="text-xs text-gray-500">
                              {hardwareStatus.camera ? 'Confirmed' : 'Please confirm your camera is visible'}
                            </p>
                          </div>
                        </div>

                        {cameraStream ? (
                          <div className="space-y-4">
                            <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
                  <video
                                ref={setCameraVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                                style={{ transform: 'scaleX(-1)' }} // Mirror the video
                  />
                </div>
                            <div className="flex justify-end">
                              <button
                                onClick={confirmCamera}
                                className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                                style={{ backgroundColor: '#f5945c', border: 'none' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                                }}
                              >
                                <span>Camera Looks Good</span>
                                <ChevronRight className="h-4 w-4" />
                    </button>
                    </div>
                  </div>
                        ) : (
                          <div className="text-center py-8">
                            <button
                              onClick={startCameraPreview}
                              className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors mx-auto"
                              style={{ backgroundColor: '#f5945c', border: 'none' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                              }}
                            >
                              <Camera className="h-4 w-4" />
                              <span>Start Camera</span>
                            </button>
                </div>
                        )}

                        {/* Reconfirm option if already verified */}
                        {hardwareStatus.camera && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <button
                              onClick={() => {
                                // Reset camera status and restart
                                setHardwareStatus(prev => ({ ...prev, camera: false }));
                                if (cameraStream) {
                                  cameraStream.getTracks().forEach(track => track.stop());
                                  setCameraStream(null);
                                }
                              }}
                              className="text-sm text-gray-600 hover:text-gray-900 underline"
                            >
                              Reconfirm Camera
                    </button>
                    </div>
                        )}
              </div>

                      {/* Navigation buttons for camera step */}
                      <div className="flex justify-between pt-4 border-t border-gray-200 mt-6">
                <button
                          onClick={() => {
                            // Go back to guidelines step
                            setCurrentStep(1);
                          }}
                          className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2.5 px-6 rounded-lg transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          <span>Back</span>
                </button>
                        {hardwareStatus.camera && (
                          <button
                            onClick={() => {
                              setHardwareSubStep('microphone');
                            }}
                            className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                            style={{ backgroundColor: '#f5945c', border: 'none' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                            }}
                          >
                            <span>Continue</span>
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {hardwareSubStep === 'microphone' && (
                    <div className="space-y-4">
                      {/* Microphone Check with Recording */}
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3 mb-4">
                          <Mic className={`h-5 w-5 ${hardwareStatus.microphone ? 'text-green-600' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium text-gray-900">Microphone</p>
                            <p className="text-xs text-gray-500">
                              {hardwareStatus.microphone ? 'Verified' : 'Not verified'}
                            </p>
                      </div>
                    </div>

                        {!hardwareStatus.microphone ? (
                          <div className="space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-sm text-gray-700 mb-2">
                                Please say the following sentence to test your microphone:
                              </p>
                              <p className="text-base font-medium text-gray-900 italic">
                                "Hello, this is my voice test for the interview."
                              </p>
                    </div>

                            {!recordedAudio ? (
                              <div className="flex items-center justify-center space-x-3">
                                {!isRecording ? (
                                  <button
                                    onClick={startMicrophoneRecording}
                                    className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                                    style={{ backgroundColor: '#f5945c', border: 'none' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                                    }}
                                  >
                                    <Mic className="h-4 w-4" />
                                    <span>Start Recording</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={stopMicrophoneRecording}
                                    className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                                  >
                                    <Square className="h-4 w-4" />
                                    <span>Stop Recording</span>
                                  </button>
                                )}
                      </div>
                            ) : (
                              <div className="space-y-3">
                                <div className="flex items-center justify-center space-x-3">
                                  {!isPlaying ? (
                                    <button
                                      onClick={playRecordedAudio}
                                      className="flex items-center space-x-2 bg-green-400 hover:bg-green-500 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                                    >
                                      <Play className="h-4 w-4" />
                                      <span>Play Recording</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={stopPlayback}
                                      className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                                    >
                                      <Square className="h-4 w-4" />
                                      <span>Stop Playback</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={resetRecording}
                                    className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2.5 px-6 rounded-lg transition-colors"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                    <span>Retry</span>
                                  </button>
                  </div>
                              </div>
                            )}

                    {isRecording && (
                              <div className="flex items-center justify-center space-x-2 text-red-600">
                                <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                                <span className="text-sm font-medium">Recording...</span>
                      </div>
                    )}

                            {/* Hidden audio element for playback */}
                            {audioUrl && (
                              <audio
                                ref={setAudioElementRef}
                                src={audioUrl}
                                onPlay={() => setIsPlaying(true)}
                                onEnded={() => {
                                  setIsPlaying(false);
                                  setAudioElementRef(null);
                                }}
                                onPause={() => setIsPlaying(false)}
                                onError={(e) => {
                                  console.error('Audio element error:', e);
                                  setIsPlaying(false);
                                  alert('Failed to play recording. Please try recording again.');
                                }}
                                style={{ display: 'none' }}
                              />
                            )}
                  </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-center justify-end">
                              <Check className="h-5 w-5 text-green-600" />
                </div>
                            {/* Reconfirm option if already verified */}
                            <div className="pt-4 border-t border-gray-200">
                              <button
                                onClick={() => {
                                  // Reset microphone status and restart
                                  setHardwareStatus(prev => ({ ...prev, microphone: false }));
                                  resetRecording();
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900 underline"
                              >
                                Reconfirm Microphone
                              </button>
              </div>
                          </div>
                        )}
            </div>

                      {/* Navigation buttons for microphone step */}
                      <div className="flex justify-between pt-4 border-t border-gray-200 mt-6">
                        <button
                          onClick={() => {
                            setHardwareSubStep('camera');
                          }}
                          className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2.5 px-6 rounded-lg transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          <span>Back</span>
                        </button>
                        {hardwareStatus.microphone ? (
                          <button
                            onClick={() => {
                              setHardwareStatus(prev => ({ ...prev, microphone: true }));
                              setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
                              setTimeout(() => {
                                setHardwareSubStep('screen');
                              }, 0);
                            }}
                            className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                            style={{ backgroundColor: '#f5945c', border: 'none' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                            }}
                          >
                            <span>Continue</span>
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        ) : recordedAudio ? (
                          <button
                            onClick={() => {
                              console.log('Moving to screen step...');
                              setHardwareStatus(prev => ({ ...prev, microphone: true }));
                              setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
                              setTimeout(() => {
                                setHardwareSubStep('screen');
                                console.log('Screen step set, hardwareStatus.screen:', false);
                              }, 0);
                            }}
                            className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                            style={{ backgroundColor: '#f5945c', border: 'none' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                            }}
                          >
                            <span>Confirm & Continue</span>
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {hardwareSubStep === 'screen' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Screen Sharing Check</h3>
                      {/* Screen Sharing Check */}
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3 mb-4">
                          <Monitor className={`h-5 w-5 ${hardwareStatus.screen ? 'text-green-600' : 'text-gray-400'}`} />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-gray-900">Screen Sharing</p>
                              {hardwareStatus.systemAudio && (
                                <span title="System audio enabled">
                                  <Volume2 className="h-4 w-4 text-green-600" />
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {hardwareStatus.screen ? (hardwareStatus.systemAudio ? 'Verified with system audio' : 'Screen verified, system audio required') : 'Click button below to start'}
                            </p>
          </div>
        </div>

                        {!hardwareStatus.screen ? (
                          <div className="space-y-4">
                            {!screenStream ? (
                              <>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                  <p className="text-sm text-yellow-800 mb-2 font-medium">
                                    Important: Single Display Required
                                  </p>
                                  <p className="text-sm text-yellow-700">
                                    Please ensure that only a single display is connected to your device. 
                                    Multiple displays are not allowed during the interview.
                                  </p>
        </div>

                                <div className="bg-gray-50 rounded-lg p-4">
                                  <p className="text-sm text-gray-700 mb-2">
                                    Click the button below to start screen sharing. The browser will show a prompt 
                                    asking you to select which screen to share.
                                  </p>
                                  <p className="text-xs text-gray-500 mb-2">
                                    <strong>Important:</strong> You <strong className="text-red-600">must select "Entire Screen"</strong> to proceed. 
                                    Selecting a window or browser tab is not allowed.
                                  </p>
                                  <p className="text-xs text-red-600 mb-2 font-semibold">
                                    <strong>Required:</strong> You <strong>must enable "Share system audio"</strong> or "Share tab audio" when prompted. 
                                    System audio is mandatory to proceed with the interview.
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    <strong>Note:</strong> If you have multiple monitors, the browser will show a selection dialog. 
                                    Make sure to select your primary display.
                                  </p>
    </div>

                                <div className="flex flex-col items-center space-y-4">
                                  <button
                                    onClick={startScreenShare}
                                    className="flex items-center space-x-2 text-white font-medium py-3 px-8 rounded-lg transition-colors shadow-md"
                                    style={{ backgroundColor: '#f5945c', border: 'none' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                                    }}
                                  >
                                    <Monitor className="h-5 w-5" />
                                    <span>Start Screen Share</span>
                                  </button>
                                  <p className="text-xs text-gray-500 text-center">
                                    Clicking this button will open the browser's screen sharing dialog
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="space-y-4">
                                <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video border-2 border-gray-300">
                                  <video
                                    ref={setScreenVideoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="w-full h-full object-contain"
                                    style={{ 
                                      transform: 'translateZ(0)', // Enable hardware acceleration
                                      willChange: 'contents' // Optimize for frequent updates
                                    }}
                                  />
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <p className="text-sm text-blue-800 mb-1 font-medium">
                                    ✓ Screen sharing is active
                                  </p>
                                  {hardwareStatus.systemAudio ? (
                                    <p className="text-xs text-green-700 mb-2 font-medium">
                                      ✓ System audio is enabled
                                    </p>
                                  ) : (
                                    <p className="text-xs text-red-700 mb-2 font-medium">
                                      ⚠️ System audio is required - Please ensure "Share system audio" is enabled
                                    </p>
                                  )}
                                  <p className="text-xs text-blue-700 mb-2">
                                    Your screen is being shared. Now verify the following:
                                  </p>
                                  <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                                    <li>Only ONE display/monitor is connected to your device</li>
                                    <li>All additional monitors have been disconnected</li>
                                    <li>The shared screen preview above shows your primary display</li>
                                    <li>System audio sharing is enabled (required)</li>
                                  </ul>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                  <p className="text-xs text-yellow-800">
                                    <strong>⚠️ Important:</strong> If you have multiple displays, please disconnect them now before continuing.
                                  </p>
                                </div>
                                <div className="flex justify-center space-x-3">
      <button
                                    onClick={confirmScreenShare}
                                    className="bg-[#f5945c] hover:bg-[#e8854b] text-white font-medium py-2.5 px-8 rounded-lg transition-colors"
      >
                                    Verify Single Display & Continue
      </button>
    </div>
  </div>
)}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-center justify-end">
                              <Check className="h-5 w-5 text-green-600" />
                            </div>
                            {/* Reconfirm option if already verified */}
                            <div className="pt-4 border-t border-gray-200">
              <button 
                                onClick={() => {
                                  // Reset screen status and restart
                                  setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
                                  if (screenStream) {
                                    screenStream.getTracks().forEach(track => track.stop());
                                    setScreenStream(null);
                                  }
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900 underline"
                              >
                                Reconfirm Screen Sharing
              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Navigation buttons for screen step */}
                      <div className="flex justify-between pt-4 border-t border-gray-200 mt-6">
                        <button
                          onClick={() => {
                            setHardwareSubStep('microphone');
                          }}
                          className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2.5 px-6 rounded-lg transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          <span>Back</span>
              </button>
                        {hardwareStatus.camera && hardwareStatus.microphone && hardwareStatus.screen && hardwareStatus.systemAudio ? (
                          <button
                            onClick={() => {
                              // Show summary before starting interview
                              setHardwareSubStep('summary');
                            }}
                            className="flex items-center space-x-2 bg-[#f5945c] hover:bg-[#e8854b] text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                          >
                            <span>View Summary</span>
                            <ChevronRight className="h-4 w-4" />
              </button>
                        ) : (
                          <button
                            disabled
                            className="flex items-center space-x-2 bg-gray-300 text-gray-500 font-medium py-2.5 px-6 rounded-lg cursor-not-allowed"
                          >
                            <span>Complete All Checks</span>
                          </button>
                        )}
            </div>
                    </div>
                  )}

                  {hardwareSubStep === 'summary' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Hardware Check Summary</h3>
                      
                      {/* All Verified Hardware Checks */}
                      <div className="space-y-3">
                        {/* Camera Check */}
                        <div className="p-4 border border-gray-200 rounded-lg bg-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Camera className="h-5 w-5 text-gray-600" />
                              <div>
                                <p className="font-medium text-gray-900">Camera</p>
                                <p className="text-xs text-gray-500">Verified</p>
                              </div>
                            </div>
                            <CheckCircle className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>

                        {/* Microphone Check */}
                        <div className="p-4 border border-gray-200 rounded-lg bg-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Mic className="h-5 w-5 text-gray-600" />
                              <div>
                                <p className="font-medium text-gray-900">Microphone</p>
                                <p className="text-xs text-gray-500">Verified</p>
                              </div>
                            </div>
                            <CheckCircle className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>

                        {/* Screen Sharing Check */}
                        <div className="p-4 border border-gray-200 rounded-lg bg-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Monitor className="h-5 w-5 text-gray-600" />
                              <div>
                                <p className="font-medium text-gray-900">Screen Sharing</p>
                                <p className="text-xs text-gray-500">Verified</p>
                              </div>
                            </div>
                            <CheckCircle className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>

                        {/* System Audio Check */}
                        <div className="p-4 border border-gray-200 rounded-lg bg-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Volume2 className="h-5 w-5 text-gray-600" />
                              <div>
                                <p className="font-medium text-gray-900">System Audio</p>
                                <p className="text-xs text-gray-500">Verified</p>
                              </div>
                            </div>
                            <CheckCircle className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>
                      </div>

                      {/* Success Message */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-gray-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              All hardware checks completed
                            </p>
                            <p className="text-xs text-gray-600">
                              Your camera, microphone, and screen sharing are verified and ready.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Reconfirm options */}
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Need to reconfirm any check?</p>
                        <div className="flex flex-wrap gap-2">
                      <button 
                            onClick={() => {
                              setHardwareStatus(prev => ({ ...prev, camera: false }));
                              if (cameraStream) {
                                cameraStream.getTracks().forEach(track => track.stop());
                                setCameraStream(null);
                              }
                              setHardwareSubStep('camera');
                            }}
                            className="text-sm text-gray-600 hover:text-gray-900 underline"
                          >
                            Reconfirm Camera
                      </button>
                          <span className="text-gray-300">|</span>
                      <button 
                            onClick={() => {
                              setHardwareStatus(prev => ({ ...prev, microphone: false }));
                              resetRecording();
                              setHardwareSubStep('microphone');
                            }}
                            className="text-sm text-gray-600 hover:text-gray-900 underline"
                          >
                            Reconfirm Microphone
                      </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => {
                              setHardwareStatus(prev => ({ ...prev, screen: false, systemAudio: false }));
                              if (screenStream) {
                                screenStream.getTracks().forEach(track => track.stop());
                                setScreenStream(null);
                              }
                              setHardwareSubStep('screen');
                            }}
                            className="text-sm text-gray-600 hover:text-gray-900 underline"
                          >
                            Reconfirm Screen Sharing
                          </button>
                        </div>
                      </div>

                      {/* Navigation buttons for summary step */}
                      <div className="flex justify-between pt-4 border-t border-gray-200 mt-6">
                      <button 
                        onClick={() => {
                            setHardwareSubStep('screen');
                        }} 
                          className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2.5 px-6 rounded-lg transition-colors"
                      >
                          <ChevronLeft className="h-4 w-4" />
                          <span>Back</span>
                      </button>
                        <button
                          onClick={() => {
                            // Signal to camera useEffect: don't stop stream when preview unmounts
                            startingInterviewRef.current = true;
                            // Keep cameraStream for recording (camera video in InterviewPage)
                            // Keep screenStream for system audio in InterviewPage
                            setShowGuidelinesModal(false);
                            setCurrentStep(1);
                            setGuidelinesChecked([]);
                            setHardwareSubStep('camera');
                            
                            // Show InterviewPage component instead of navigating
                            console.log('Starting interview, showing InterviewPage component');
                            console.log('📹 Screen stream state before starting:', screenStream ? 'available' : 'null');
                            if (screenStream) {
                              console.log('📹 Screen stream tracks:', {
                                video: screenStream.getVideoTracks().length,
                                audio: screenStream.getAudioTracks().length,
                                active: screenStream.active
                              });
                            }
                            setShowInterviewPage(true);
                          }}
                          className="flex items-center space-x-2 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
                          style={{ 
                            backgroundColor: '#f5945c',
                            border: 'none',
                            color: 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.setProperty('background-color', '#e8854a', 'important');
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.setProperty('background-color', '#f5945c', 'important');
                          }}
                          id="start-interview-button"
                        >
                          <span>Start Interview</span>
                          <ChevronRight className="h-4 w-4" />
                    </button>
            </div>
          </div>
        )}
      </div>
              )}
    </div>
          </div>
        </div>
      )}

      {/* Screen Selection Error Modal */}
      {showScreenSelectionErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200">
            {/* Modal Header */}
            <div className="border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Invalid Screen Selection</h2>
              <button
                onClick={() => setShowScreenSelectionErrorModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-red-700 mb-2 font-medium">
                  Multiple Displays Detected
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  Please disconnect additional monitors and ensure only one display is connected, then try sharing again.
                </p>
                <p className="text-sm text-gray-600">
                  For the interview, you must use only a single display/monitor connected to your device.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 flex justify-end">
              <button
                onClick={() => setShowScreenSelectionErrorModal(false)}
                className="bg-gray-800 hover:bg-gray-900 text-white font-medium py-2 px-6 rounded-lg transition-colors"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Display Confirmation Modal */}
      {showSingleDisplayConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200">
            {/* Modal Header */}
            <div className="border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Confirm Single Display</h2>
              <button
                onClick={() => {
                  if (singleDisplayConfirmCallback) {
                    singleDisplayConfirmCallback(false);
                    setSingleDisplayConfirmCallback(null);
                  }
                  setShowSingleDisplayConfirmModal(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  Please confirm the following:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 mb-4">
                  <li>Do you have only <strong>ONE</strong> display/monitor connected to your device?</li>
                  <li>Have you disconnected any additional monitors?</li>
                </ol>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-yellow-800">
                    <strong>Important:</strong> Multiple displays are not allowed during the interview. Please ensure only one monitor is connected before proceeding.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  if (singleDisplayConfirmCallback) {
                    singleDisplayConfirmCallback(false);
                    setSingleDisplayConfirmCallback(null);
                  }
                  setShowSingleDisplayConfirmModal(false);
                }}
                className="bg-[#f7a97a] hover:bg-[#f5945c] text-white font-medium py-2 px-6 rounded-lg transition-colors"
              >
                I Have Multiple Displays
              </button>
              <button
                onClick={() => {
                  if (singleDisplayConfirmCallback) {
                    singleDisplayConfirmCallback(true);
                    setSingleDisplayConfirmCallback(null);
                  }
                  setShowSingleDisplayConfirmModal(false);
                }}
                className="bg-[#e8854b] hover:bg-[#d7763a] text-white font-medium py-2 px-6 rounded-lg transition-colors"
              >
                I Have Only One Display
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Interview;
